/**
 * Super Admin route wrapper for the sealed USB compatibility matrix.
 *
 * The compatibility page is authored ONCE as a self-contained static
 * HTML file at /usb-preview/scoring/compatibility.html so it ships on
 * every sealed drive. This React wrapper simply frames the same file
 * inside the Super Admin chrome via an <iframe>, which:
 *
 *   1. Avoids duplicating the ~500-line matrix + feature-detection JS
 *      in two places (drive + web app).
 *   2. Guarantees IT admins reviewing the page online see the exact
 *      same content that will run on the physical drive — including
 *      the LIVE feature detection running against the admin's browser.
 *
 * The route is /admin/sealed-usb-compatibility. Access is gated by the
 * router-level ProtectedRoute for super_admin.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

const SealedUsbCompatibility = () => {
  // Cache-buster tied to build so hot-reload picks up static edits
  // without a full hard-refresh cycle.
  const src = useMemo(
    () => `/usb-preview/scoring/compatibility.html?embed=admin&v=${Date.now()}`,
    []
  );

  return (
    <div
      style={{ padding: "24px 20px", background: "#F7F8FB", minHeight: "100vh" }}
      data-testid="sealed-usb-compat-page"
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
            gap: 12,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 22, letterSpacing: "-.01em" }}>
              Sealed USB · Compatibility
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
              Live device probe + hand-tested device matrix. Same page ships
              on every drive so IT admins can double-click it before ordering.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Link
              to="/admin"
              data-testid="compat-back-to-admin"
              style={{
                padding: "8px 14px",
                background: "#FFFFFF",
                border: "1px solid #CBD5E1",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                color: "#0F172A",
                textDecoration: "none",
              }}
            >
              ← Super Admin
            </Link>
            <a
              href="/usb-preview/scoring/compatibility.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="compat-open-standalone"
              style={{
                padding: "8px 14px",
                background: "#1D4ED8",
                color: "#FFFFFF",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Open standalone ↗
            </a>
          </div>
        </div>

        <iframe
          title="Sealed USB Compatibility Matrix"
          src={src}
          data-testid="sealed-usb-compat-iframe"
          style={{
            width: "100%",
            height: "calc(100vh - 130px)",
            border: "1px solid #E4E7EC",
            borderRadius: 12,
            background: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(15,23,42,.04)",
          }}
        />
      </div>
    </div>
  );
};

export default SealedUsbCompatibility;
